import * as connectionsApi from "@/src/api/connections";

// Requests in flight per store, so components mounting together (one graph wizard per workspace
// card) share one call instead of each sending their own before the first answer arrives.
const inFlight = new WeakMap();

const shared = (state, key, request) => {
    let requests = inFlight.get(state);
    if (!requests) inFlight.set(state, requests = {});
    if (!requests[key]) {
        const pending = request();
        requests[key] = pending;
        const clear = () => { if (requests[key] === pending) requests[key] = null; };
        pending.then(clear, clear);
    }
    return requests[key];
};

// Connectors (pac-eventio, with their JSON Schemas) and the data connections of this environment.
export default {
    namespaced: true,
    state: {
        connectors: [],
        connections: [],
        connectorsLoaded: false,
        connectionsLoaded: false,
    },
    mutations: {
        setConnectors: (state, connectors) => {
            state.connectors = connectors || [];
            state.connectorsLoaded = true;
        },
        setConnections: (state, connections) => {
            state.connections = connections || [];
            state.connectionsLoaded = true;
        },
    },
    actions: {
        // Payloads are optional: `dispatch('connectionStore/loadAll', null, {root: true})` passes null.
        loadConnectors: async ({state, commit}, options) => {
            const force = Boolean(options?.force);
            if (state.connectorsLoaded && !force) return state.connectors;
            return shared(state, force ? 'connectorsForced' : 'connectors', async () => {
                commit('setConnectors', await connectionsApi.listConnectors());
                return state.connectors;
            });
        },
        loadConnections: async ({state, commit}, options) => {
            const force = Boolean(options?.force);
            if (state.connectionsLoaded && !force) return state.connections;
            return shared(state, force ? 'connectionsForced' : 'connections', async () => {
                commit('setConnections', await connectionsApi.listConnections());
                return state.connections;
            });
        },
        loadAll: async ({dispatch}, options) => {
            await Promise.all([dispatch('loadConnectors', options), dispatch('loadConnections', options)]);
        },
        getConnection: async (_, connectionId) => {
            return await connectionsApi.getConnection(connectionId);
        },
        saveConnection: async ({dispatch}, {connectionId, name, connectorType, connection}) => {
            const saved = connectionId
                ? await connectionsApi.updateConnection(connectionId, {name, connection})
                : await connectionsApi.createConnection({name, connector_type: connectorType, connection});
            await dispatch('loadConnections', {force: true});
            return saved;
        },
        deleteConnection: async ({dispatch}, connectionId) => {
            await connectionsApi.deleteConnection(connectionId);
            await dispatch('loadConnections', {force: true});
        },
        testConnection: async (_, {connectionId, connectorType, connection}) => {
            return await connectionsApi.testConnection({id: connectionId, connector_type: connectorType, connection});
        },
        loadOptions: async (_, {connectionId, provider, params}) => {
            return await connectionsApi.listConnectionOptions(connectionId, provider, params);
        },
    },
    getters: {
        connectors: (state) => state.connectors,
        connections: (state) => state.connections,
        connector: (state) => (type) => state.connectors.find(connector => connector.type === type) || null,
        connection: (state) => (id) => state.connections.find(connection => connection.id === id) || null,
        connectorOfConnection: (state, getters) => (id) => {
            const connection = getters.connection(id);
            return connection ? getters.connector(connection.connector_type) : null;
        },
    },
};
